import { useQuery } from '@apollo/client';
import { EmptyOrLoadingState, ScrollToTop } from '@motivo/guanyin/src/components';
import authRoutes from '@motivo/styx/src/authRoutes';
import { getIsHandlingAuthFailure } from '@motivo/styx/src/auth';
import { AUTH_CALLBACK_PATHNAME } from '@motivo/styx/src/config';
import useSetUpAnalytics from '@motivo/styx/src/useSetUpAnalytics';
import { StyledEngineProvider } from '@mui/material/styles';
import React, { Suspense, lazy, useEffect } from 'react';
import { Route as ReactRouterRoute, RouteProps, Switch, useLocation } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import EmailVerificationPage from '@motivo/guanyin/src/components/EmailVerificationPage';
import { ThemeProvider } from './ThemeProvider';
import ErrorBoundary from '@motivo/guanyin/src/components/ErrorBoundary';
import { ResultOf } from './graphql';
import Layout from './components/Layout';
import SettingsLayout from './components/SettingsLayout';
import AuthenticatedRoute from './routing/AuthenticatedRoute';
import { editProgramSuperviseesPath } from './domains/programs/EditSuperviseesPage';
import { SelectedProgramFilterProvider } from './domains/programs/SelectedProgramFilterContext';
import { programSettingsPagePath } from './domains/settings/ProgramSettingsPage';
import { viewerQuery } from './hooks/useViewer';
import SnackbarState from './hooks/SnackbarState';
import { MotivoBasicGateProvider } from './domains/basic/useMotivoBasicGate';

const DashboardPage = lazy(() => import('./domains/dashboard/DashboardPage'));
const SessionsPage = lazy(() => import('./domains/sessions'));
const SupervisorsPage = lazy(() => import('./domains/supervisors/SupervisorsPage'));
const SupervisorProfilePage = lazy(() => import('./domains/supervisors/SupervisorProfilePage'));
const SuperviseeProfilePage = lazy(() => import('./domains/supervisees/SuperviseeProfilePage'));
const SuperviseesPage = lazy(() => import('./domains/supervisees/SuperviseesPage'));
const BasicResourcesPage = lazy(() => import('./domains/basic/BasicResourcesPage'));
const ProgramsPage = lazy(() => import('./domains/programs/ProgramsPage'));
const EditProgramSuperviseesPage = lazy(() => import('./domains/programs/EditSuperviseesPage'));
const ProgramSettingsPage = lazy(() => import('./domains/settings/ProgramSettingsPage'));

const PrepForCallPage = lazy(() => import('./domains/onboarding/PrepForCallPage'));
const ScheduleCallPage = lazy(() => import('./domains/onboarding/ScheduleCallPage'));
const MyIntroRequestsPage = lazy(() => import('./domains/introRequests/IntroRequestsPage'));
const ScheduleCallSuccessPage = lazy(() => import('./domains/onboarding/ScheduleCallSuccessPage'));
const IntroRequestDetailsPage = lazy(
  () => import('./domains/introRequests/IntroRequestDetailsPage'),
);
const BookIntroCallPage = lazy(() => import('./domains/introRequests/BookIntroCallPage'));
const SuperviseeCSVUploadPage = lazy(() => import('./domains/supervisees/SuperviseeCSVUploadPage'));
const SupervisorCSVUploadPage = lazy(() => import('./domains/supervisors/SupervisorCSVUploadPage'));
const SuperviseeManualUploadPage = lazy(
  () => import('./domains/supervisees/SuperviseeManualUploadPage'),
);
const PublicSuperviseeSignupPage = lazy(
  () => import('./domains/supervisees/PublicSuperviseeSignupPage'),
);
const OrganizationIntakePage = lazy(
  () => import('./domains/organizationIntake/OrganizationIntakePage'),
);

const AdminHoursPage = lazy(() => import('./domains/admin-hours/AdminHoursPage'));
const GeneralSettingsPage = lazy(() => import('./domains/settings/GeneralPage'));
const ChatsPage = lazy(() => import('./domains/messages/ChatsPage'));
const TeamPage = lazy(() => import('./domains/settings/TeamPage'));
const ManageNotificationsPage = lazy(() => import('./domains/settings/ManageNotificationsPage'));
const BillingPage = lazy(() => import('./domains/settings/BillingPage'));
const ResetPasswordPage = lazy(() => import('./domains/settings/ResetPasswordPage'));
const ConfirmEmailChangePage = lazy(() => import('./domains/settings/ConfirmEmailChangePage'));

const NotFoundPage = lazy(() => import('@motivo/guanyin/src/components/NotFoundPage'));

const OnboardingLayout = lazy(() => import('./domains/basic/OnboardingLayout'));
const OnboardingWelcomePage = lazy(() => import('./domains/basic/OnboardingWelcomePage'));
const OnboardingAdminInfoPage = lazy(() => import('./domains/basic/OnboardingAdminInfoPage'));
const OnboardingEmployerInfoPage = lazy(() => import('./domains/basic/OnboardingEmployerInfoPage'));
const OnboardingCompletePage = lazy(() => import('./domains/basic/OnboardingCompletePage'));
const ReviewAndSendPage = lazy(() => import('./domains/basic/ReviewAndSendPage'));
const OrganizationAdminWelcomePage = lazy(() => import('./domains/welcome/OrganizationAdminWelcomePage'));

interface RoutePropsWithTitle extends RouteProps {
  title?: string;
}

function Route({ children, title, ...rest }: RoutePropsWithTitle) {
  useEffect(() => {
    if (title) {
      document.title = `${title} | Motivo for Employers`;
    }
  }, [title]);

  return <ReactRouterRoute {...rest}>{children}</ReactRouterRoute>;
}

const App = () => {
  const location = useLocation();
  // Preload the viewer so it can be assumed to be available everywhere.
  const { data, loading, refetch } = useQuery(viewerQuery, {
    skip: location.pathname === AUTH_CALLBACK_PATHNAME,
  });
  const viewer = data?.viewer;

  useSetUpAnalytics<ResultOf<typeof viewerQuery>['viewer']>({
    user: viewer,
    afterSetUser: ({ posthog, user }) => {
      // @ts-expect-error TS(2533) FIXME: Object is possibly 'null' or 'undefined'.
      posthog.group('organization', user.organizationId.toString(), {
        // @ts-expect-error TS(2533) FIXME: Object is possibly 'null' or 'undefined'.
        name: user.organization.name,
      });
    },
    getAdditionalProperties: (user) => ({
      organizationRoleId: user.organizationRoleId,
    }),
  });

  if (loading) {
    return <EmptyOrLoadingState loading />;
  }

  return (
    <SnackbarProvider>
      <SnackbarState.Provider>
        <StyledEngineProvider injectFirst>
          <ThemeProvider>
            <ScrollToTop>
              <Suspense fallback={<EmptyOrLoadingState loading />}>
                <Switch>
                  <Route path="/reset-password" exact>
                    <ResetPasswordPage />
                  </Route>
                  <Route path="/email-verification" exact>
                    <EmailVerificationPage />
                  </Route>
                  <Route path="/confirm-email-change" exact>
                    <ConfirmEmailChangePage />
                  </Route>
                  <Route path="/employer-supervisee-signup/:slug" exact>
                    <PublicSuperviseeSignupPage />
                  </Route>
                  <Route path="/employer-intake/:token" exact>
                    <OrganizationIntakePage />
                  </Route>
                  <Route path="/welcome" exact>
                    <OnboardingLayout>
                      <OrganizationAdminWelcomePage />
                    </OnboardingLayout>
                  </Route>
                  {/* Motivo Basic onboarding routes */}
                  <Route path="/onboarding">
                    <OnboardingLayout>
                      <Switch>
                        <Route path="/onboarding" exact>
                          <OnboardingWelcomePage />
                        </Route>
                        <Route path="/onboarding/basic-info" exact>
                          <OnboardingAdminInfoPage />
                        </Route>
                        <AuthenticatedRoute path="/onboarding/employer-info" exact>
                          <OnboardingEmployerInfoPage />
                        </AuthenticatedRoute>
                        <AuthenticatedRoute path="/onboarding/complete" exact>
                          <OnboardingCompletePage />
                        </AuthenticatedRoute>
                      </Switch>
                    </OnboardingLayout>
                  </Route>
                  {authRoutes({ fetchViewer: refetch })}
                  <SelectedProgramFilterProvider>
                    <MotivoBasicGateProvider>
                      <Layout>
                        <ErrorBoundary getIsRecovering={getIsHandlingAuthFailure}>
                          <Suspense fallback={<EmptyOrLoadingState loading />}>
                            <Switch>
                              <AuthenticatedRoute exact path="/">
                                <DashboardPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/sessions" exact>
                                <SessionsPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/supervisees" exact>
                                <SuperviseesPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/supervisees/upload" exact>
                                <SuperviseeCSVUploadPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/supervisees/upload-form" exact>
                                <SuperviseeManualUploadPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/supervisors" exact>
                                <SupervisorsPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/supervisors/upload" exact>
                                <SupervisorCSVUploadPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/supervisors/:id/:tab?" exact>
                                <SupervisorProfilePage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/supervisees/:id/:tab?" exact>
                                <SuperviseeProfilePage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/resources" exact>
                                <BasicResourcesPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/review-and-send" exact>
                                <ReviewAndSendPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/programs" exact>
                                <ProgramsPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute
                                path={programSettingsPagePath(':programId')}
                                exact
                              >
                                <ProgramSettingsPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path={editProgramSuperviseesPath(':id')} exact>
                                <EditProgramSuperviseesPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute
                                path="/employer-onboarding/prep-for-call/:introRequestOrganizationInfoId?"
                                exact
                              >
                                <PrepForCallPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute
                                path="/employer-onboarding/schedule-call/success"
                                exact
                              >
                                <ScheduleCallSuccessPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute
                                path="/employer-onboarding/schedule-call/:introRequestOrganizationInfoId"
                                exact
                              >
                                <ScheduleCallPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/intro-calls" exact>
                                <MyIntroRequestsPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/intro-calls/:id" exact>
                                <IntroRequestDetailsPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute
                                path="/intro-calls/create/:opportunitiesCandidateId"
                                exact
                              >
                                <BookIntroCallPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/chats/:id?" exact>
                                <ChatsPage />
                              </AuthenticatedRoute>
                              <AuthenticatedRoute path="/admin-hours" exact>
                                <AdminHoursPage />
                              </AuthenticatedRoute>

                              <AuthenticatedRoute path="/settings">
                                <SettingsLayout>
                                  <Suspense fallback={<EmptyOrLoadingState loading />}>
                                    <AuthenticatedRoute path="/settings" exact>
                                      <GeneralSettingsPage />
                                    </AuthenticatedRoute>
                                    <AuthenticatedRoute path="/settings/billing" exact>
                                      <BillingPage />
                                    </AuthenticatedRoute>
                                    <AuthenticatedRoute path="/settings/notifications" exact>
                                      <ManageNotificationsPage />
                                    </AuthenticatedRoute>
                                    <AuthenticatedRoute path="/settings/team" exact>
                                      <TeamPage />
                                    </AuthenticatedRoute>
                                  </Suspense>
                                </SettingsLayout>
                              </AuthenticatedRoute>

                              <Route path="*">
                                <NotFoundPage />
                              </Route>
                            </Switch>
                          </Suspense>
                        </ErrorBoundary>
                      </Layout>
                    </MotivoBasicGateProvider>
                  </SelectedProgramFilterProvider>
                </Switch>
              </Suspense>
            </ScrollToTop>
          </ThemeProvider>
        </StyledEngineProvider>
      </SnackbarState.Provider>
    </SnackbarProvider>
  );
};

export default App;
